import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { parseData, getBase, getFormat } from '../helpers.js';
import { confirm } from '../prompt.js';

export const relationshipsCommand = new Command('relationships')
  .description('Manage relationships between documents');

relationshipsCommand.command('upsert')
  .description('Create or update a relationship')
  .requiredOption('--source <record_type/id>', 'Source document (record_type/id)')
  .requiredOption('--target <record_type/id>', 'Target document (record_type/id)')
  .requiredOption('--type <type>', 'Relationship type')
  .option('--id <id>', 'Relationship ID')
  .option('--data <json>', 'Relationship metadata (inline JSON or @filename)')
  .action(async function (this: Command, opts: { source: string; target: string; type: string; id?: string; data?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const [sourceRecordType, sourceId] = opts.source.split('/');
    const [targetRecordType, targetId] = opts.target.split('/');
    const body: Record<string, unknown> = {
      rel_type: opts.type,
      source_record_type: sourceRecordType,
      source_id: sourceId,
      target_record_type: targetRecordType,
      target_id: targetId,
    };
    if (opts.id) body['id'] = opts.id;
    if (opts.data) body['data'] = parseData(opts.data);
    const path = opts.id ? `/v1/${ws}/relationships/${opts.id}` : `/v1/${ws}/relationships`;
    const data = await client.request('PUT', path, body);
    console.log(formatOutput(data, getFormat(this)));
  });

relationshipsCommand.command('get <id>')
  .description('Get a relationship by ID')
  .action(async function (this: Command, id: string) {
    const ws = getBase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/relationships/${id}`);
    console.log(formatOutput(data, getFormat(this)));
  });

relationshipsCommand.command('delete <id>')
  .description('Delete a relationship')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
    if (!opts.yes && !(await confirm(`Delete relationship ${id}? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const ws = getBase(this);
    const client = new ApiClient();
    await client.request('DELETE', `/v1/${ws}/relationships/${id}`);
    console.log('Deleted');
  });

relationshipsCommand.command('history <id>')
  .description('View the change history (audit log) for a relationship — every version, who changed it, and when')
  .option('--limit <n>', 'Max results')
  .option('--offset <n>', 'Pagination offset')
  .option('--diff', 'Include field-level diffs between versions')
  .action(async function (this: Command, id: string, opts: { limit?: string; offset?: string; diff?: boolean }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const qs = new URLSearchParams();
    if (opts.limit) qs.set('limit', opts.limit);
    if (opts.offset) qs.set('offset', opts.offset);
    if (opts.diff) qs.set('diff', 'true');
    const q = qs.toString();
    const data = await client.request('GET', `/v1/${ws}/audit/relationships/${id}${q ? `?${q}` : ''}`);
    console.log(formatOutput(data, getFormat(this)));
  });
